import { useInstanceStore } from 'src/stores/instance.js'
import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'

// What the server tells about itself, as the values the stores take:
// { instance: { path: value }, capabilities: { name: value } }. Pure, so
// each field can be tested; applyInstanceState puts them in the stores.

// what the server did not say: left out, so the stores keep their defaults
const given = (values) =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  )

// /api/v1/instance; a server without Pleroma's block (Mastodon) still
// gives its limits and rules
export const instanceToState = (data) => {
  const pleroma = data.pleroma
  return {
    instance: given({
      'limits.textLimit': data.max_toot_chars,
      accountApprovalRequired: data.approval_required,
      birthdayRequired: !!pleroma?.metadata?.birthday_required,
      rules: data.rules ?? [],
      birthdayMinAge: pleroma?.metadata?.birthday_min_age || 0,
      vapidPublicKey: pleroma?.vapid_public_key || undefined,
    }),
    capabilities: given({ pleromaExtensionsAvailable: pleroma }),
  }
}

// /nodeinfo/2.x.json; other servers leave parts out, and what is there
// still counts
export const nodeInfoToState = (data) => {
  const metadata = data.metadata ?? {}
  const software = data.software ?? {}
  const features = metadata.features ?? []
  const has = (feature) => features.includes(feature)
  const federation = metadata.federation ?? {}
  const uploadLimits = metadata.uploadLimits
  const suggestions = metadata.suggestions ?? {}
  const bubble = metadata.localBubbleInstances ?? []
  const limit = (name) =>
    uploadLimits?.[name] === undefined
      ? undefined
      : Number.parseInt(uploadLimits[name])
  return {
    instance: given({
      'instanceIdentity.name': metadata.nodeName,
      registrationOpen: data.openRegistrations,
      localBubbleInstances: bubble,
      'limits.pollLimits': metadata.pollLimits,
      'limits.uploadlimit': limit('general'),
      'limits.avatarlimit': limit('avatar'),
      'limits.backgroundlimit': limit('background'),
      'limits.bannerlimit': limit('banner'),
      'limits.fieldsLimits': metadata.fieldsLimits,
      restrictedNicknames: metadata.restrictedNicknames,
      backendVersion: software.version,
      backendRepository: software.repository,
      privateMode: metadata.private,
      frontendVersion: window.___pleromafe_commit_hash,
      federationPolicy: metadata.federation,
      federating: federation.enabled === undefined ? true : federation.enabled,
      accountActivationRequired: metadata.accountActivationRequired,
      // the nicknames, from the actor URIs
      staffAccounts: (metadata.staffAccounts ?? []).map((uri) =>
        uri.split('/').pop(),
      ),
    }),
    capabilities: given({
      mediaProxyAvailable: has('media_proxy'),
      safeDM: has('safe_dm_mentions'),
      shoutAvailable: has('chat'),
      pleromaChatMessagesAvailable: has('pleroma_chat_messages'),
      directoryAvailable: has('profile_directory'),
      pinChatsAvailable: has('pleroma:pin_chats'),
      pleromaCustomEmojiReactionsAvailable:
        has('pleroma_custom_emoji_reactions') || has('custom_emoji_reactions'),
      pleromaBookmarkFoldersAvailable: has('pleroma:bookmark_folders'),
      gopherAvailable: has('gopher'),
      pollsAvailable: has('polls'),
      editingAvailable: has('editing'),
      mailerEnabled: metadata.mailerEnabled,
      quotingAvailable: has('quote_posting'),
      groupActorAvailable: has('pleroma:group_actors'),
      blockExpiration: has('pleroma:block_expiration'),
      localBubble: bubble.length > 0,
      postFormats: metadata.postFormats,
      suggestionsEnabled: suggestions.enabled,
      suggestionsWeb: suggestions.web,
      tagPolicyAvailable:
        federation.mrf_policies?.includes('TagPolicy') ?? false,
    }),
  }
}

// each value on its own: one the store refuses (a null where a value is
// required) is logged, and the rest still count
export const applyInstanceState = ({ instance = {}, capabilities = {} }) => {
  const instanceStore = useInstanceStore()
  const capabilityStore = useInstanceCapabilitiesStore()
  const apply = (name, set) => {
    try {
      set()
    } catch (e) {
      console.warn(`Could not use the server's ${name}`, e)
    }
  }
  for (const [path, value] of Object.entries(instance)) {
    apply(path, () => instanceStore.set({ path, value }))
  }
  for (const [name, value] of Object.entries(capabilities)) {
    apply(name, () => capabilityStore.set(name, value))
  }
}
